import { useMemo, useState } from 'react';
import { DataRowExporter, useLocalStorageState } from 'commons/utils';
import { Button, Table } from 'semantic-ui-react';
import { useBlockTagCounts } from 'api/tagsApi';
import { PlaceHolderSegment } from 'components/PlaceholderSegment';
import { Spinner } from 'components/spinner';
import { Link } from 'react-router-dom';
import { BlockTagDeleteModal } from './components/BlockTagDeleteModal';
import { BlockTagReplaceModal } from './components/BlockTagReplaceModal';
import { getSearchModePreference } from '../blockUtils';
import { buildTagOverviewSearch, isNewSearchDefault, preferNewSearch } from './blockSearch';

const LOCAL_STORAGE_KEY_SORT_COLUMN = 'blockTagsOverview_sortColumn';
const LOCAL_STORAGE_KEY_SORT_DIRECTION = 'blockTagsOverview_sortDirection';

type TagCountEntry = {
  tag: string;
  count: number;
};

type SortColumn = 'tag' | 'count';
type SortDirection = 'ascending' | 'descending';

export function BlockTagsOverview() {
  const tagsQuery = useBlockTagCounts();
  const [tagToDelete, setTagToDelete] = useState<TagCountEntry | null>(null);
  const [tagToReplace, setTagToReplace] = useState<TagCountEntry | null>(null);
  const { value: sortColumn, setValue: setSortColumn } = useLocalStorageState<SortColumn>(LOCAL_STORAGE_KEY_SORT_COLUMN, 'count');
  const { value: sortDirection, setValue: setSortDirection } = useLocalStorageState<SortDirection>(LOCAL_STORAGE_KEY_SORT_DIRECTION, 'descending');

  const tags = useMemo<TagCountEntry[]>(() => {
    if (!tagsQuery.data) {
      return [];
    }

    const items = Object.entries(tagsQuery.data).map(([tag, count]) => ({ tag, count }));

    return items.sort((a, b) => {
      if (sortColumn === 'count') {
        if (a.count !== b.count) {
          return sortDirection === 'ascending' ? a.count - b.count : b.count - a.count;
        }

        return a.tag.localeCompare(b.tag);
      }

      const tagSort = a.tag.localeCompare(b.tag);
      if (tagSort !== 0) {
        return sortDirection === 'ascending' ? tagSort : -tagSort;
      }

      return b.count - a.count;
    });
  }, [tagsQuery.data, sortColumn, sortDirection]);

  const toggleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'ascending' ? 'descending' : 'ascending');
      return;
    }

    setSortColumn(column);
    setSortDirection(column === 'count' ? 'descending' : 'ascending');
  };

  const handleDownloadTsv = () => {
    const header = ['Tag', 'Count'];
    const rows = tags.map((item) => [item.tag, `${item.count}`]);
    const exporter = new DataRowExporter([header, ...rows]);
    // exporter.includeSeperatorMetadata = true;
    exporter.DownloadAsTsv('block_tags');
  };

  if (tagsQuery.isIdle) {
    return <PlaceHolderSegment iconName="key" header="Awaiting authentication token..." />;
  }

  if (tagsQuery.isLoading) {
    return <Spinner visible text="Loading block tags..." />;
  }

  if (tagsQuery.isError) {
    const message = tagsQuery.error instanceof Error ? tagsQuery.error.message : 'Unknown error';

    return (
      <PlaceHolderSegment iconName="exclamation triangle" header="An error happened while loading block tags :/">
        <pre>{message}</pre>
      </PlaceHolderSegment>
    );
  }

  if (tags.length === 0) {
    return <PlaceHolderSegment iconName="tags" header="No aggregate block tags found." />;
  }

  return (
    <>
      <BlockTagDeleteModal tagEntry={tagToDelete} onClose={() => setTagToDelete(null)} />
      <BlockTagReplaceModal tagEntry={tagToReplace} onClose={() => setTagToReplace(null)} />

      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <p style={{ margin: 0 }}>Found {tags.length} aggregate block tags</p>
          <Button basic icon="download" content="Download TSV" labelPosition="left" onClick={handleDownloadTsv} />
        </div>
        <Table celled sortable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell sorted={sortColumn === 'tag' ? sortDirection : undefined} onClick={() => toggleSort('tag')}>
                Tag
              </Table.HeaderCell>
              <Table.HeaderCell textAlign="right" sorted={sortColumn === 'count' ? sortDirection : undefined} onClick={() => toggleSort('count')}>
                Count
              </Table.HeaderCell>
              <Table.HeaderCell collapsing>Actions</Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {tags.map((item) => (
              <Table.Row key={item.tag}>
                <Table.Cell>
                  <Link
                    to={{
                      pathname: '/block/overview',
                      search: buildTagOverviewSearch(item.tag, preferNewSearch(getSearchModePreference(), isNewSearchDefault())),
                    }}
                  >
                    {item.tag}
                  </Link>
                </Table.Cell>
                <Table.Cell textAlign="right">{item.count}</Table.Cell>
                <Table.Cell collapsing>
                  <Button.Group size="small">
                    <Button negative basic onClick={() => setTagToDelete(item)}>
                      Delete
                    </Button>
                    <Button primary basic onClick={() => setTagToReplace(item)}>
                      Replace
                    </Button>
                  </Button.Group>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
    </>
  );
}
